import React, { useState, useEffect } from 'react';

const WallModal = ({ isOpen, onClose, onSubmit, wall = null }) => {
  // Form state
  const [wallName, setWallName] = useState('');
  const [wallWidth, setWallWidth] = useState(120);
  const [wallHeight, setWallHeight] = useState(96);
  const [isLoading, setIsLoading] = useState(false);
  
  // Set initial values when editing an existing wall
  useEffect(() => {
    if (wall) {
      setWallName(wall.source || '');
      setWallWidth(wall.width || 120);
      setWallHeight(wall.height || 96);
    } else {
      // Reset form when adding a new wall
      setWallName('');
      setWallWidth(120);
      setWallHeight(96);
    }
  }, [wall, isOpen]);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await onSubmit({
        name: wallName,
        width: wallWidth,
        height: wallHeight
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  
  console.log('🔧 WallModal render:', { isOpen, wall });
  if (!isOpen) {
    console.log('🔧 Modal not open, returning null');
    return null;
  }
  console.log('🔧 Modal is open, rendering...');
  
  return (
    <div 
      className="wall-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="wall-modal-title"
    >
      <div className="wall-modal-container">
        {/* Header */}
        <div className="wall-modal-header">
          <h3 id="wall-modal-title" className="wall-modal-title">
            {wall ? 'Edit Wall' : 'Add New Wall'}
          </h3>
          <p className="wall-modal-subtitle">
            {wall ? 'Update wall dimensions and name' : 'Create a new wall for your gallery'}
          </p>
        </div>
        
        {/* Body */}
        <div className="wall-modal-body">
          <form onSubmit={handleSubmit}>
            {/* Wall Name */}
            <div className="wall-form-group">
              <label className="wall-form-label" htmlFor="wall-name">
                Wall Name
              </label>
              <input 
                id="wall-name"
                type="text" 
                className="wall-form-input"
                value={wallName}
                onChange={(e) => setWallName(e.target.value)}
                placeholder="e.g., North Wall, Main Exhibition Wall"
                required
                autoFocus
              />
              <p className="wall-form-helper">
                Give your wall a descriptive name for easy identification
              </p>
            </div>
            
            {/* Dimensions Grid */}
            <div className="wall-dimensions-grid">
              <div className="wall-form-group">
                <label className="wall-form-label" htmlFor="wall-width">
                  Width
                </label>
                <div className="wall-input-with-icon">
                  <input 
                    id="wall-width"
                    type="number" 
                    className="wall-form-input has-icon"
                    min="12"
                    max="480"
                    value={wallWidth}
                    onChange={(e) => setWallWidth(Number(e.target.value))}
                    required
                  />
                  <span className="wall-input-icon">inches</span>
                </div>
                <p className="wall-form-helper">
                  12" - 480" (1-40 feet)
                </p>
              </div>
              
              <div className="wall-form-group">
                <label className="wall-form-label" htmlFor="wall-height">
                  Height
                </label>
                <div className="wall-input-with-icon">
                  <input 
                    id="wall-height"
                    type="number" 
                    className="wall-form-input has-icon"
                    min="12"
                    max="240"
                    value={wallHeight}
                    onChange={(e) => setWallHeight(Number(e.target.value))}
                    required
                  />
                  <span className="wall-input-icon">inches</span>
                </div>
                <p className="wall-form-helper">
                  12" - 240" (1-20 feet)
                </p>
              </div>
            </div>
            
            {/* Form Buttons */}
            <div className="wall-form-buttons">
              <button 
                type="button"
                className="wall-btn wall-btn-cancel"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                type="submit"
                className={`wall-btn wall-btn-save ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {wall ? 'Update Wall' : 'Create Wall'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WallModal;